/**
 * usePhotoActions — TanStack Query mutations for photo publish/delete flows.
 *
 * Endpoints (Slice 2/3, gg-app #222 + #224):
 *   POST   /v1/photos/:id/publish-preview  → 202 { photoId, status:"rendering" }
 *   PATCH  /v1/photos/:id                  → body { public_confirmed: bool }
 *   DELETE /v1/photos/:id                  → 204 (crypto-shred, permanent)
 *
 * After any mutation the photo list query is invalidated so the tile grid
 * reflects the new state immediately.
 */

import { photoKeys } from '@/hooks/usePhotos';
import { apiFetch, apiFetchEmpty } from '@/lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

const PublishPreviewResponseSchema = z.object({
  photoId: z.string(),
  status: z.literal('rendering'),
});

export type PublishPreviewResponse = z.infer<typeof PublishPreviewResponseSchema>;

/** POST /v1/photos/:id/publish-preview — triggers async derivative render. */
export function usePublishPreview(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) =>
      apiFetch<PublishPreviewResponse>(
        `/v1/photos/${encodeURIComponent(photoId)}/publish-preview`,
        { method: 'POST', schema: PublishPreviewResponseSchema },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: photoKeys(vehicleId) });
    },
  });
}

/**
 * PATCH /v1/photos/:id — confirm or revoke public visibility.
 * confirmed=true  → publish (requires ready derivative, else backend 400)
 * confirmed=false → make private
 */
export function useConfirmPublish(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ photoId, confirmed }: { photoId: string; confirmed: boolean }) =>
      apiFetch(`/v1/photos/${encodeURIComponent(photoId)}`, {
        method: 'PATCH',
        body: { public_confirmed: confirmed },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: photoKeys(vehicleId) });
    },
  });
}

/**
 * DELETE /v1/photos/:id — crypto-shred. Permanent, irreversible.
 * Returns 204 No Content on success. Photo bytes are erased everywhere
 * including any shared links.
 */
export function useDeletePhoto(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) =>
      apiFetchEmpty(`/v1/photos/${encodeURIComponent(photoId)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: photoKeys(vehicleId) });
    },
  });
}
